import React, { useState } from 'react';
import '../styles/CoworkPanel.css';

const API_URL = 'https://veritan-api.veritan.workers.dev/api';

const CHIEFS = [
  { mode: 'GOVERNANCE',   agent: 'VELA',    icon: '🎯', desc: 'Strategy' },
  { mode: 'TECHNOLOGY',   agent: 'VULCAN',  icon: '⚙️', desc: 'Tech' },
  { mode: 'CAPITAL',      agent: 'MONETA',  icon: '💰', desc: 'Capital' },
  { mode: 'LEGAL',        agent: 'FIDIUS',  icon: '⚖️', desc: 'Legal' },
  { mode: 'BRAND',        agent: 'VENUS',   icon: '🎨', desc: 'Brand' },
  { mode: 'OPERATIONS',   agent: 'OPERA',   icon: '📋', desc: 'Ops' },
  { mode: 'MARKETING',    agent: 'MERCURY', icon: '📢', desc: 'Marketing' },
  { mode: 'GROWTH',       agent: 'FAUNUS',  icon: '📈', desc: 'Growth' },
  { mode: 'PARTNERSHIPS', agent: 'JANUS',   icon: '🤝', desc: 'Partnerships' },
  { mode: 'CUSTOMER',     agent: 'VESTA',   icon: '❤️', desc: 'Customer' },
];

const SPECIALISTS = [
  { mode: 'AUDIT',    agent: 'CUSTOS', icon: '🔍', desc: 'Audit' },
  { mode: 'REALWORLD', agent: 'CURA',  icon: '🌍', desc: 'Real World' },
  { mode: 'TIMING',   agent: 'KAIROS', icon: '⏱️', desc: 'Timing' },
  { mode: 'AIINFRA',  agent: 'NESTOR', icon: '🧠', desc: 'AI Infra' },
];

const ALL_MODES = [...CHIEFS, ...SPECIALISTS];

interface CoworkResult {
  mode: string;
  agent: string;
  response?: string;
  error?: string;
  duration_ms?: number;
}

interface HistoryEntry {
  message: string;
  results: CoworkResult[];
}

const getModeInfo = (mode: string) => ALL_MODES.find(m => m.mode === mode);

export default function CoworkPanel() {
  const [selected, setSelected] = useState<string[]>(['GOVERNANCE', 'TECHNOLOGY', 'CAPITAL']);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CoworkResult[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeHistoryIdx, setActiveHistoryIdx] = useState<number | null>(null);

  function toggleMode(mode: string) {
    setSelected(prev =>
      prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]
    );
  }

  async function send() {
    const q = message.trim();
    if (!q || !selected.length || loading) return;
    setLoading(true);
    setResults([]);
    setActiveHistoryIdx(null);
    setMessage('');

    try {
      const res = await fetch(`${API_URL}/cowork-parallel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, modes: selected }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const r: CoworkResult[] = data.results || [];
      setResults(r);
      const entry = { message: q, results: r };
      setHistory(prev => [entry, ...prev.slice(0, 19)]);
    } catch (err: any) {
      const errResults: CoworkResult[] = selected.map(mode => ({
        mode,
        agent: getModeInfo(mode)?.agent || mode,
        error: err.message || 'Request failed',
      }));
      setResults(errResults);
    } finally {
      setLoading(false);
    }
  }

  function loadHistory(idx: number) {
    const entry = history[idx];
    if (!entry) return;
    setResults(entry.results);
    setActiveHistoryIdx(idx);
  }

  const displayResults = results;

  const colClass = displayResults.length <= 2
    ? 'cowork-cols-2'
    : displayResults.length === 3
    ? 'cowork-cols-3'
    : 'cowork-cols-3';

  return (
    <div className="cowork-panel">
      {/* Agent selector */}
      <div className="cowork-toolbar">
        <div className="cowork-agent-selector">
          <div className="cw-agent-row">
            <span className="cw-group-label">CHIEFS</span>
            {CHIEFS.map(({ mode, agent, icon }) => (
              <button
                key={mode}
                className={`cw-chip${selected.includes(mode) ? ' active' : ''}`}
                onClick={() => toggleMode(mode)}
                title={mode}
              >
                {icon} {agent}
              </button>
            ))}
          </div>
          <div className="cw-agent-row">
            <span className="cw-group-label">SPECIALISTS</span>
            {SPECIALISTS.map(({ mode, agent, icon }) => (
              <button
                key={mode}
                className={`cw-chip${selected.includes(mode) ? ' active' : ''}`}
                onClick={() => toggleMode(mode)}
                title={mode}
              >
                {icon} {agent}
              </button>
            ))}
            <button
              className="cw-chip cw-chip-action"
              onClick={() => setSelected(selected.length === ALL_MODES.length ? [] : ALL_MODES.map(m => m.mode))}
            >
              {selected.length === ALL_MODES.length ? '✕ Clear all' : '⚡ All 14'}
            </button>
          </div>
        </div>
        <div className="cowork-input-row">
          <textarea
            className="cowork-input"
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={
              selected.length === 0
                ? 'Select agents above first...'
                : `Ask ${selected.length} agent${selected.length > 1 ? 's' : ''} in parallel... (Enter to send)`
            }
            rows={1}
            disabled={loading}
          />
          <button
            className="cowork-send-btn"
            onClick={send}
            disabled={loading || !message.trim() || selected.length === 0}
          >
            {loading ? <span className="cw-spin">⟳</span> : '⚡'}
            <span>{loading ? 'Running...' : 'Ask All'}</span>
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="cowork-body">
        {/* Left: history */}
        {history.length > 0 && (
          <div className="cowork-history">
            <div className="cw-history-title">History</div>
            {history.map((h, i) => (
              <div
                key={i}
                className={`cw-history-item${activeHistoryIdx === i ? ' active' : ''}`}
                onClick={() => loadHistory(i)}
                title={h.message}
              >
                <span className="cw-history-q">{h.message.slice(0, 60)}{h.message.length > 60 ? '…' : ''}</span>
                <span className="cw-history-meta">{h.results.length} agents</span>
              </div>
            ))}
          </div>
        )}

        {/* Right: results */}
        <div className="cowork-results-area">
          {loading && (
            <div className="cw-loading">
              <div className="cw-loading-spinner" />
              <span>Querying {selected.length} agents simultaneously…</span>
            </div>
          )}

          {!loading && displayResults.length > 0 && (
            <div className={`cowork-cards ${colClass}`}>
              {displayResults.map(r => {
                const info = getModeInfo(r.mode);
                return (
                  <div key={r.mode} className={`cw-card${r.error ? ' cw-card-error' : ''}`}>
                    <div className="cw-card-header">
                      <span className="cw-card-title">
                        {info?.icon} {r.agent}
                      </span>
                      {r.duration_ms && (
                        <span className="cw-card-time">{(r.duration_ms / 1000).toFixed(1)}s</span>
                      )}
                    </div>
                    <div className="cw-card-body">
                      {r.error
                        ? <span className="cw-error-msg">{r.error}</span>
                        : <div className="cw-response">{r.response}</div>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && displayResults.length === 0 && (
            <div className="cw-empty">
              <div className="cw-empty-icon">⚡</div>
              <div className="cw-empty-title">Multi-Agent Cowork</div>
              <p>Select experts above, type a question, press Enter.<br />All agents respond in parallel.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
